import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CATALOG_ITEMS, getUnitPrice } from "../data/catalog";

const CartContext = createContext(null);
const STORE_KEY = "nara_cart_v2";

// Cart lines are minimal: { id, mode, quantity }. Display data is derived from the catalog.
const normalize = (raw) =>
  Array.isArray(raw)
    ? raw
        .filter((i) => i && CATALOG_ITEMS[i.id])
        .map((i) => ({
          id: i.id,
          mode: i.mode === "subscribe" && CATALOG_ITEMS[i.id].subscribable ? "subscribe" : "once",
          quantity: Math.max(1, i.quantity || 1),
        }))
    : [];

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return normalize(JSON.parse(localStorage.getItem(STORE_KEY)));
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (id, mode = "once", quantity = 1) => {
    const meta = CATALOG_ITEMS[id];
    if (!meta) return;
    const m = mode === "subscribe" && meta.subscribable ? "subscribe" : "once";
    setItems((prev) => {
      const existing = prev.find((i) => i.id === id && i.mode === m);
      if (existing) {
        return prev.map((i) => (i.id === id && i.mode === m ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [...prev, { id, mode: m, quantity }];
    });
    setIsOpen(true);
  };

  const updateQty = (id, mode, quantity) => {
    if (quantity <= 0) return removeItem(id, mode);
    setItems((prev) => prev.map((i) => (i.id === id && i.mode === mode ? { ...i, quantity } : i)));
  };

  const removeItem = (id, mode) => setItems((prev) => prev.filter((i) => !(i.id === id && i.mode === mode)));
  const clearCart = () => setItems([]);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + getUnitPrice(i.id, i.mode) * i.quantity, 0), [items]);

  const value = { items, isOpen, setIsOpen, addItem, updateQty, removeItem, clearCart, count, subtotal };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
